import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { formatShortDateTime } from '@/utils/date';
import { humanize } from '@/utils/format';
import type { TaskDetailResponse } from '@/types/task';
import type { TaskRowAction } from './TasksTable';
import {
  SubtaskStatusBadge,
  SubtaskStatusIcon,
  TaskStatusIndicator,
  TaskTypeBadge,
} from './TaskStatus';

export interface TaskDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskDetailResponse | null;
  isLoading: boolean;
  error: string | null;
  actions: Record<TaskRowAction, boolean>;
  isBusy: (action: TaskRowAction) => boolean;
  onAction: (action: TaskRowAction) => void;
}

/**
 * Collapsible output of a subtask (certbot, `nginx -t`, agent responses)
 */
function SubtaskLog({ log }: { log: string }) {
  return (
    <details className="tasks-subtask-log">
      <summary>Log</summary>
      <pre>{log}</pre>
    </details>
  );
}

/**
 * Task details with its subtasks and cancel/retry actions.
 * A running task (e.g. after "Retry Failed") is refreshed by the caller
 * until it has finished.
 */
export function TaskDetailModal({
  isOpen,
  onClose,
  task,
  isLoading,
  error,
  actions,
  isBusy,
  onAction,
}: TaskDetailModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader>Task Details</ModalHeader>
      <ModalBody>
        {isLoading ? (
          <p className="tasks-detail-loading">Loading...</p>
        ) : error ? (
          <div className="modal-error" role="alert">
            {error}
          </div>
        ) : task ? (
          <>
            <div className="tasks-detail-section">
              <div className="tasks-detail-grid">
                <div className="tasks-detail-item">
                  <span className="tasks-detail-label">Type</span>
                  <span className="tasks-detail-value">
                    <TaskTypeBadge type={task.type} />
                  </span>
                </div>
                <div className="tasks-detail-item">
                  <span className="tasks-detail-label">Status</span>
                  <span className="tasks-detail-value">
                    <TaskStatusIndicator status={task.status} />
                  </span>
                </div>
                <div className="tasks-detail-item">
                  <span className="tasks-detail-label">Created</span>
                  <span className="tasks-detail-value">{formatShortDateTime(task.created_at)}</span>
                </div>
                <div className="tasks-detail-item">
                  <span className="tasks-detail-label">Completed</span>
                  <span className="tasks-detail-value">{formatShortDateTime(task.completed_at)}</span>
                </div>
              </div>
              {task.description && (
                <div className="tasks-detail-item" style={{ marginTop: '0.75rem' }}>
                  <span className="tasks-detail-label">Description</span>
                  <span className="tasks-detail-value">{task.description}</span>
                </div>
              )}
              {task.error && (
                <div className="tasks-detail-error" style={{ marginTop: '0.75rem' }}>
                  {task.error}
                </div>
              )}
            </div>

            <div className="tasks-detail-section">
              <h4 className="tasks-subtask-title">
                Subtasks ({task.completed_subtasks}/{task.total_subtasks}
                {task.failed_subtasks > 0 && `, ${task.failed_subtasks} failed`})
              </h4>
              <div className="tasks-subtask-list">
                {task.subtasks?.map((subtask) => (
                  <div key={subtask.id} className="tasks-subtask-item">
                    <SubtaskStatusIcon status={subtask.status} />
                    <div className="tasks-subtask-content">
                      <div className="tasks-subtask-header">
                        <span className="tasks-subtask-type">{humanize(subtask.type)}</span>
                        <SubtaskStatusBadge status={subtask.status} />
                      </div>
                      {subtask.description && <div className="tasks-subtask-desc">{subtask.description}</div>}
                      {subtask.error && <div className="tasks-subtask-error">{subtask.error}</div>}
                      {subtask.log && <SubtaskLog log={subtask.log} />}
                      <div className="tasks-subtask-meta">
                        {subtask.started_at && `Started: ${formatShortDateTime(subtask.started_at)}`}
                        {subtask.completed_at && ` | Done: ${formatShortDateTime(subtask.completed_at)}`}
                        {subtask.retry_count > 0 && ` | Retries: ${subtask.retry_count}/${subtask.max_retries}`}
                      </div>
                    </div>
                  </div>
                ))}
                {(!task.subtasks || task.subtasks.length === 0) && (
                  <p className="tasks-subtask-empty">No subtasks</p>
                )}
              </div>
            </div>
          </>
        ) : null}
      </ModalBody>
      <ModalFooter>
        {task && actions.retry && (
          <Button variant="secondary" onClick={() => onAction('retry')} isLoading={isBusy('retry')}>
            Retry Failed
          </Button>
        )}
        {task && actions.cancel && (
          <Button variant="danger" onClick={() => onAction('cancel')} isLoading={isBusy('cancel')}>
            Cancel Task
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}
